import {
  startTransition,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import MemoCardButton from "@components/atoms/memo-card-button";
import { MemoInterface, WriterInterface } from "@carglassgithub/memod-sdk";
import { interpolatedTextString } from "@models/interactions/interpolate-text";
import CreateCollectionModal from "@components/molecules/create-collection-modal";
import Icon from "@components/atoms/icon";
import { useAtomValue, useStore } from "jotai";
import { translate } from "@locales";
import { sdk, sdkv1 } from "@models/api";
import SaveMemoPopover, {
  SaveMemoItem,
} from "@components/molecules/save-memo-popover";
import { UserLoggedAtom } from "@models/state/atoms/user-logged.atom";
import {
  AUTH_STATUS,
  AuthModalAtom,
} from "@models/state/atoms/auth-modal.atom";
import useAmplitude from "@models/interactions/use-amplitude";
import DownDrop from "@components/atoms/drop-down";
import classNames from "classnames";
import icon from "@components/atoms/icon";
import Cookies from "cookie-universal";
import { toast } from "react-toastify";
import {
  MemoLikeAtom,
  MemoSaveAtom,
  UserAtom,
  UserSaveItemsAtom,
} from "@models/state";
import { useRouter } from "next/router";

interface Props {
  memo: MemoInterface;
  likes?: number;
  saves?: number;
  onShare?: () => void;
  commentsCount?: number;
  onComment?: () => void;
  onSaves?: (saves: number) => void;
  onLikes?: (likes: number) => void;
  toggleShowShare?: () => void;
  onCreateCollection?: (value: MemoInterface) => void;
  ownerMemo?: boolean;
  onSave?: (data: MemoInterface[]) => void;
  profileUser?: WriterInterface;
}

interface option {
  text?: string;
  onClick?: () => void;
  icon: JSX.Element;
}

const ITEMS: SaveMemoItem[] = [
  {
    Icon: Icon.Add,
    text: translate("base.saveBoard"),
    key: translate("base.save"),
  },
];

const cookies = Cookies();

const memoIcon = [<icon.Pencil size={16} />, <icon.GlobeFill size={16} />];

export function useInteractionFooter(props: Props) {
  const { operations } = useAmplitude();
  const [like, toggleLike] = useState(false);
  const [save, setSave] = useState(false);
  const [isPublish, setIsPublish] = useState(props.memo.is_published);
  const [showCreate, setShowCreate] = useState(false);
  const [items, setItem] = useState<SaveMemoItem[]>(ITEMS);
  const logged = useAtomValue(UserLoggedAtom);
  const user = useAtomValue(UserAtom);
  const store = useStore();

  const router = useRouter();

  const boards = useAtomValue(UserSaveItemsAtom).sort((a, b) =>
    a.text.localeCompare(b.text)
  );

  const isLike = useAtomValue(MemoLikeAtom);
  const isSaved = useAtomValue(MemoSaveAtom);

  const interpolatedText = (key: string, amount?: number) => {
    return translate(key, { interpolate: { amount: amount || 0 } });
  };

  async function getBoards() {
    if (!user) {
      store.set(AuthModalAtom, {
        mode: AUTH_STATUS.LOGIN,
        open: true,
      });
      return;
    }

    if (boards.length > 1) {
      return;
    }

    const getBoards = await sdkv1.memodUsers.getBoards(user.id, {
      limit: 25,
      page: 1,
      memo_id: props.memo.id,
    });

    const transformedBoards = getBoards.data
      .map<SaveMemoItem>((board) => ({
        key: board.id,
        text: board.list_name,
        saved: board.is_inside === 1,
      }))
      .filter((board) => board.text !== "Unlocked Memos");

    store.set(UserSaveItemsAtom, (current) => [
      ...current,
      ...transformedBoards.reverse(),
    ]);
  }

  const toggleCreate = () => {
    if (user) {
      operations.send("Clicked Component", {
        name: "clicked-create-collection",
        title: "Clicked Create Collection",
        user_id: user.id,
        path: router.asPath,
        click_at: Date.now()
      });
    }
    setShowCreate(!showCreate);
  }

  const deleteMemo = async () => {
    try {
      await sdk.http.http.delete(`/memos/${props.memo.id}`, {
        headers: {
          authorization: cookies.get("token"),
        },
      });

      const { data } = await sdkv1.http.http.get(
        `/users/${props.profileUser?.id}/feeds?&format=true&light=true&limit=25&page=1&sort=id%7Cdesc`,
        {
          headers: {
            authorization: cookies.get("token"),
          },
        },
      );
      props.onSave?.(data.data);
    } catch (e) {
      console.log(e);
    }
  };

  const unPublish = async () => {
    const switchStatus = isPublish === 1 ? 0 : 1;
    try {
      await sdk.http.http.put(
        `/memos/${props.memo.id}/status`,
        { is_published: switchStatus },
        {
          headers: {
            authorization: cookies.get("token"),
          },
        },
      );

      setIsPublish(switchStatus);

      toast(
        [
          interpolatedTextString(
            "base.status-memo",
            "",
            switchStatus ? "publish" : "unpublish",
          ),
        ]
          .flat()
          .join(""),
        {
          icon: <Icon.NotificationCheck />,
        },
      );
    } catch (e) {
      console.log(e);
    }
  };

  const editMemo = () => {
    window.location.href = `/edit/${props.memo.slug}-${props.memo.id}`;
  };

  const handleItem = async (item: SaveMemoItem) => {
    if (logged) {
      if (item.text === "Save to new collection") {
        toggleCreate();
        return;
      }

      store.set(UserSaveItemsAtom, (current) => {
        const index = current.findIndex((i) => i.key === item.key);
        current[index] = { ...item, loading: true };
        return [...current];
      });

      const response = await sdkv1.feeds.save({
        list_id: item.key,
        storm_id: props.memo.id,
      });

      setSave(response.is_saved === 1);

      operations.send(
        response.is_saved ? "Saved Entity" : "Remove Saved Entity",
        {
          [response.is_saved ? "saved_at" : "remove_saved_at"]: Date.now(),
          entity_owner_id: props.memo.writer.id,
          entity_id: props.memo.id,
          entity_name: props.memo?.title,
          entity_type: "memo",
        },
      );

      toast(
        response.is_saved ? `Saved to collection` : `Removed from collection`, //pass to translate.
        {
          icon: <Icon.NotificationCheck />,
        },
      );

      store.set(UserSaveItemsAtom, (current) => {
        const index = current.findIndex((i) => i.key === item.key);
        current[index] = {
          ...item,
          loading: false,
          saved: response.is_inside === 1,
        };

        const saved = current[index].saved;

        store.set(MemoSaveAtom, !!current.find((i) => i.saved));
        return [...current];
      });

      props.onSaves?.(response.memo_total_saved);
    } else {
      operations.send("Unknown User Clicked Component", {
        entity_id: props.memo.id,
        entity_type: "memo",
        entity_name: props.memo?.title,
        entity_owner_id: props.memo.writer.id,
        user_id: operations.getUserId,
        unknown_user_id: operations.getUserId,
        path: router.asPath,
        click_at: Date.now()
      })
      store.set(AuthModalAtom, {
        mode: AUTH_STATUS.SIGN_UP,
        open: true,
      });
    }
  };

  const handleLike = async () => {
    if (logged) {
      const likes = await sdkv1.feeds.likeMemo(props.memo.id);
      toggleLike((state) => !state);
      props.onLikes?.(likes.total);
    } else {
      store.set(AuthModalAtom, {
        mode: AUTH_STATUS.SIGN_UP,
        open: true,
      });
    }
  };

  useEffect(() => {
    startTransition(() => {
      setSave(props.memo.is_saved === 1);
      toggleLike(props.memo.is_liked === 1 || !!props.memo.is_liked);
    });

    return () =>
      startTransition(() => {
        setSave(false);
        toggleLike(false);
      });
  }, [props.memo.total_saved, props.memo.is_liked, props.memo.is_saved]);

  const memoUnpublish: option[] = [
    {
      text: `Edit Memo`,
      onClick: () => editMemo(),
      icon: <icon.Edit size={30} />,
    },
    {
      text: `Publish Memo`,
      onClick: () => unPublish(),
      icon: <icon.Lock size={25} height={25} />,
    },
    {
      text: `Delete`,
      onClick: () => deleteMemo(),
      icon: <icon.Trash size={30} height={30} />,
    },
  ];

  const memoPublish: option[] = [
    {
      text: `Edit Memo`,
      onClick: () => editMemo(),
      icon: <icon.Edit size={30} />,
    },
    {
      onClick: () => getBoards(),
      icon: (
        <SaveMemoPopover
          items={boards}
          onItem={handleItem}
          className="!flex !w-full !justify-between"
        >
          <div className="!text-white" onClick={() => getBoards()}>
            Move to Collection
          </div>
          <MemoCardButton
            Icon={<Icon.Save size={20} height={20} active={save} />}
            textClassName="!text-sm"
            text={props.saves}
          />
        </SaveMemoPopover>
      ),
    },
    {
      text: `Unpublish Memo`,
      onClick: () => unPublish(),
      icon: <icon.Lock size={25} height={25} />,
    },
    {
      text: `Delete`,
      onClick: () => deleteMemo(),
      icon: <icon.Trash size={30} height={30} />,
    },
  ];

  return {
    models: {
      like,
      save,
      items,
      logged,
      boards,
      memoPublish,
      memoUnpublish,
      isSaved,
      isPublish,
      showCreate,
    },
    operations: {
      handleLike,
      handleItem,
      interpolatedText,
      getBoards,
      setSave,
      setShowCreate,
      toggleCreate,
    },
  };
}

export default function InteractionFooterCard(props: Props) {
  const { models, operations } = useInteractionFooter(props);

  return (
    <footer className="mb-0 flex h-[39px] w-full flex-row items-center justify-between pl-[4px] pr-3">
      {models.showCreate && (
        <CreateCollectionModal
          open
          memo={props.memo}
          setToggleCreateModal={operations.toggleCreate}
          save={operations.setSave}
          onSaves={props.onSaves}
        />
      )}
      <section
        className={classNames(
          "relative flex h-[39px] w-[78px] flex-row items-center justify-between gap-1 px-2",
          { hidden: props.ownerMemo },
        )}
      >
        <div className="flex h-[33px] w-[33px] flex-row justify-start">
          <MemoCardButton
            Icon={<Icon.Like size={20} height={20} active={models.like} />}
            text={props.likes || ""}
            onClick={operations.handleLike}
            active={models.like}
            className="flex flex-row !justify-start"
          />
        </div>
        <div className="h-[33px] w-[33px]">
          <SaveMemoPopover
            items={models.boards}
            onItem={operations.handleItem}
            className="w-full flex-1 !justify-start gap-1.5"
            position="top"
          >
            <MemoCardButton
              Icon={<Icon.Save size={20} height={20} active={models.save} />}
              textClassName="!text-sm"
              text={props.saves || 0}
              onClick={() => operations.getBoards()}
            />
          </SaveMemoPopover>
        </div>
      </section>
      <section
        className={classNames(
          "flex h-[30px] w-[30px] cursor-pointer flex-row items-center justify-between",
          { hidden: props.ownerMemo || !models.logged },
        )}
      >
        <MemoCardButton
          onClick={props.toggleShowShare}
          Icon={<Icon.ThreeDots size={20} height={20} fill="#EBEBF54D" />}
        />
      </section>
      {props.ownerMemo && (
        <div className="flex w-full flex-row justify-between">
          <p className=" flex flex-row items-center gap-[2px] text-sm text-[#EBEBF54D]">
            {translate("base.memo-card-state")[models.isPublish]}
            {memoIcon[models.isPublish]}
          </p>
          <DownDrop
            iconButton={
              <Icon.ThreeDots size={20} height={20} fill="#EBEBF54D" />
            }
            options={models.isPublish === 1
              ? models.memoPublish
              : models.memoUnpublish}
            position="center"
            optionClassName="h-[56px] !items-center"
          />
        </div>
      )}
    </footer>
  );
}
